import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { withTranslation } from 'react-i18next';
import { sprintf } from 'sprintf-js';
import { withFormik } from 'formik/dist/index';

import Icon from 'common/components/styleguide/Icon';
import List from 'common/components/styleguide/List';
import Radio from 'common/components/styleguide/form/Radio';
import formikWithAutoSubmit from 'common/hoc/formikWithAutoSubmit';
import {
  getCurrencySymbolSelector,
  getThemeConfigSelector
} from 'common/selectors';
import { capfl } from 'common/utils';

import SS from 'common/components/category/filters/_style';


class PriceForm extends PureComponent {
  state = { isHidden: true };
  toggleIsHidden = e => {
    e.preventDefault();
    const { isHidden } = this.state;
    this.setState({ isHidden: !isHidden });
  };
  onClickClear = e => {
    e.preventDefault();
    const { setFieldValue } = this.props;
    setFieldValue('priceRange', null);
  };
  computePriceRanges = () => {
    const { data } = this.props;
    const { minPrice = 0, maxPrice = 0 } = data;
    if (minPrice === maxPrice) {
      return [];
    }
    const price = maxPrice - minPrice;
    const priceRanges = [];
    const firstPrice =
      (maxPrice < 400
        ? Math.round(price * 0.2)
        : Math.round((price * 0.2) / 100) * 100) + minPrice;
    priceRanges.push([minPrice, firstPrice]);
    const secondPrice =
      (maxPrice < 400
        ? Math.round(price * 0.4)
        : Math.round((price * 0.4) / 100) * 100) + minPrice;
    priceRanges.push([firstPrice, secondPrice]);
    const thirdPrice =
      (maxPrice < 400
        ? Math.round(price * 0.6)
        : Math.round((price * 0.6) / 100) * 100) + minPrice;
    priceRanges.push([secondPrice, thirdPrice]);
    const fourthPrice =
      (maxPrice < 400
        ? Math.round(price * 0.8)
        : Math.round((price * 0.8) / 100) * 100) + minPrice;
    priceRanges.push([thirdPrice, fourthPrice]);
    priceRanges.push([fourthPrice, maxPrice]);
    return priceRanges;
  };
  componentDidMount() {
    const { addFilterResolve } = this.props;
    if (addFilterResolve) {
      addFilterResolve();
    }
  }
  render() {
    const {
      t,
      filterTitle,
      handleSubmit,
      currencySymbol,
      setFieldValue,
      data
    } = this.props;
    const { isHidden } = this.state;
    const { selectedPriceFrom, selectedPriceTo } = data;
    const priceRanges = this.computePriceRanges();
    if (!_.isEmpty(priceRanges)) {
      return (
        <SS.FilterSection>
          <SS.FilterTitle onClick={this.toggleIsHidden}>
            {capfl(t(filterTitle))}
            <SS.TitleIcon>
              {isHidden ? (
                <Icon className="nc-ic_keyboard_arrow_down_48px" />
              ) : (
                <Icon className="nc-ic_keyboard_arrow_up_48px" />
              )}
            </SS.TitleIcon>
          </SS.FilterTitle>
          <SS.FormWrapper isHidden={isHidden}>
            <form onSubmit={handleSubmit}>
              <List reset>
                {_.map(priceRanges, (priceRange, i) => {
                  const filterName = 'priceRange';
                  const filterSelected =
                    priceRange[0] === selectedPriceFrom &&
                    priceRange[1] === selectedPriceTo;
                  const id = sprintf(
                    '%1$s-%2$s',
                    filterName,
                    _.join(priceRange, '-')
                  );
                  const filterWrapperKey = sprintf('%1$s-wrapper', id);
                  const filterKey = sprintf('%1$s', id);
                  let filterLabel = (
                    <SS.LabelText>
                      {sprintf(
                        '%1$s%3$s - %2$s%3$s',
                        priceRange[0],
                        priceRange[1],
                        currencySymbol
                      )}
                    </SS.LabelText>
                  );
                  if (i === 0) {
                    filterLabel = (
                      <SS.LabelText>
                        {sprintf(
                          '%1$s %2$s%3$s',
                          t('to'),
                          priceRange[1],
                          currencySymbol
                        )}
                      </SS.LabelText>
                    );
                  }
                  if (i === priceRanges.length - 1) {
                    filterLabel = (
                      <SS.LabelText>
                        {sprintf(
                          '%1$s %2$s%3$s',
                          t('over'),
                          priceRange[0],
                          currencySymbol
                        )}
                      </SS.LabelText>
                    );
                  }
                  return (
                    <SS.ListItem key={filterWrapperKey}>
                      <Radio
                        key={filterKey}
                        name={filterName}
                        options={[
                          { label: filterLabel, value: priceRange.join('-') }
                        ]}
                        setValueChecked={{
                          value: priceRange.join('-'),
                          isChecked: filterSelected
                        }}
                        onChange={() => {
                          setFieldValue(filterName, priceRange);
                        }}
                      />
                    </SS.ListItem>
                  );
                })}
              </List>
              {_.isNumber(selectedPriceFrom) && _.isNumber(selectedPriceTo) ? (
                <SS.ShowMore href="#" onClick={this.onClickClear}>
                  {capfl(t('clear price range'))}
                </SS.ShowMore>
              ) : (
                ''
              )}
            </form>
          </SS.FormWrapper>
        </SS.FilterSection>
      );
    }
    return null;
  }
}

const fromInitalValues = {
  priceRange: null
};

const formikEnhancer = withFormik({
  enableReinitialize: true,
  mapPropsToValues: props => {
    const { data } = props;
    const { selectedPriceFrom, selectedPriceTo } = data;
    if (_.isNumber(selectedPriceFrom) && _.isNumber(selectedPriceTo)) {
      return { priceRange: [selectedPriceFrom, selectedPriceTo] };
    }
    return fromInitalValues;
  },
  handleSubmit: (values, { props, setSubmitting }) => {
    const { submitFilters } = props;
    const { priceRange } = values;
    if (submitFilters) {
      if (!_.isEmpty(priceRange)) {
        submitFilters({ priceRange: _.join(priceRange, '-') });
      } else {
        submitFilters({ priceRange: null });
      }
    }
    setSubmitting(false);
  }
});

const EnhancedPriceForm = formikEnhancer(formikWithAutoSubmit(PriceForm));

const makeMapStateToProps = () => {
  const getThemeConfig = getThemeConfigSelector();
  const getCurrencySymbol = getCurrencySymbolSelector();
  return (state, props) => {
    return {
      config: getThemeConfig(state, props),
      currencySymbol: getCurrencySymbol(state, props)
    };
  };
};

const EnhancedPriceFormConnected = connect(makeMapStateToProps)(
  EnhancedPriceForm
);

export default withTranslation()(EnhancedPriceFormConnected);
